import {
  Alert,
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  notification,
} from "antd";
import { useContext, useEffect, useState } from "react";

import "../styles.css";

import { Context } from "../../../context";
import { Fetch, Put } from "../../../fetch";
import { convertIdToKey } from "../../../utils";
import { putSeriesCoinFields } from "../coinsApi";

const { Column } = Table;

/**
 * Daily watch LADDER (per-series): a series enabled below shows the
 * escalating daily ladder INSTEAD of its Watch & Earn card. Numbers
 * (X/Y/levels) are global; the switch picks which series run it. Resets at
 * IST midnight; rewards credit automatically the moment a level completes.
 */
const WatchLadderConfig = () => {
  const { token, apiUrl } = useContext(Context);

  const [saving, setSaving] = useState(false);
  const [series, setSeries] = useState([]);
  const [togglingId, setTogglingId] = useState(null);
  const [counter, setCounter] = useState(0);
  // Global numbers mirrored into state so the table can show each series'
  // EFFECTIVE values (override ?? global, levels capped by episode count).
  const [globalCfg, setGlobalCfg] = useState({
    episodesPerRung: 0,
    baseCoins: 0,
    maxRungs: 0,
  });
  const [modalVisible, setModalVisible] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [seriesId, setSeriesId] = useState();
  const [seriesTitle, setSeriesTitle] = useState("");

  const [form] = Form.useForm();
  const [overrideForm] = Form.useForm();

  useEffect(() => {
    Fetch(`${apiUrl}/v4/webseries?pageSize=100000`, token, (result) => {
      setSeries(convertIdToKey(result.webseries || []));
    });
  }, [apiUrl, token, counter]);

  const toggleSeries = (record, checked) => {
    setTogglingId(record.key);
    putSeriesCoinFields(
      apiUrl,
      token,
      record.key,
      { watchLadderEnabled: checked },
      (result) => {
        setTogglingId(null);
        if (result.success) {
          notification.success({
            message: "Success",
            description: `Ladder ${checked ? "enabled" : "disabled"} for ${record.title}`,
          });
          setCounter((c) => c + 1);
        } else {
          notification.error({
            message: "Error updating series",
            description: "Check console for more details",
          });
        }
      },
      () => {
        setTogglingId(null);
        notification.error({
          message: "Error updating series",
          description: "Check console for more details",
        });
      }
    );
  };

  useEffect(() => {
    Fetch(`${apiUrl}/v4/tasks-config/config/watch_ladder_config`, token, (result) => {
      if (result && result.success !== false) {
        const data = result.data || {};
        form.setFieldsValue({
          enabled: data.enabled ?? false,
          episodesPerRung: data.episodesPerRung ?? 0,
          baseCoins: data.baseCoins ?? 0,
          maxRungs: data.maxRungs ?? 3,
          infoPointsText: (data.infoPoints || []).join("\n"),
          infoPointsHiText: (data.infoPointsHi || []).join("\n"),
        });
        setGlobalCfg({
          episodesPerRung: data.episodesPerRung ?? 0,
          baseCoins: data.baseCoins ?? 0,
          maxRungs: data.maxRungs ?? 3,
        });
      }
    });
  }, [apiUrl, token, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      Put(
        `${apiUrl}/v4/tasks-config/config/watch_ladder_config`,
        token,
        {
          data: {
            enabled: values.enabled ?? false,
            episodesPerRung: values.episodesPerRung ?? 0,
            baseCoins: values.baseCoins ?? 0,
            maxRungs: values.maxRungs ?? 3,
            infoPoints: (values.infoPointsText || "")
              .split("\n")
              .map((line) => line.trim())
              .filter(Boolean),
            infoPointsHi: (values.infoPointsHiText || "")
              .split("\n")
              .map((line) => line.trim())
              .filter(Boolean),
          },
        },
        (result) => {
          setSaving(false);
          if (result.success) {
            setGlobalCfg({
              episodesPerRung: values.episodesPerRung ?? 0,
              baseCoins: values.baseCoins ?? 0,
              maxRungs: values.maxRungs ?? 3,
            });
            notification.success({
              message: "Success",
              description: "Watch ladder config updated",
            });
          } else {
            notification.error({
              message: "Error updating watch ladder config",
              description: "Check console for more details",
            });
          }
        },
        () => {
          setSaving(false);
          notification.error({
            message: "Error updating watch ladder config",
            description: "Check console for more details",
          });
        }
      );
    } catch (err) {
      notification.error({
        message: "Error updating watch ladder config",
        description: "Fields are incorrect/missing",
      });
    }
  };

  // Effective per-series numbers, mirroring the server: override ?? global,
  // levels additionally capped at numberOfEpisodes / X (an episode counts
  // once per day, so a ladder can't need more episodes than the series has).
  const effectiveNumbers = (record) => {
    const x = record.watchLadderEpisodesPerLevel || globalCfg.episodesPerRung || 0;
    const y = record.watchLadderBaseCoins || globalCfg.baseCoins || 0;
    const configured = record.watchLadderLevels || globalCfg.maxRungs || 0;
    const totalEpisodes = record.numberOfEpisodes || 0;
    const levels = x > 0 ? Math.min(configured, Math.floor(totalEpisodes / x)) : 0;
    const overridden = Boolean(
      record.watchLadderEpisodesPerLevel ||
        record.watchLadderBaseCoins ||
        record.watchLadderLevels
    );
    return { x, y, configured, levels, overridden };
  };

  const showOverrideModal = (record) => {
    setSeriesId(record.key);
    setSeriesTitle(record.title);
    setModalVisible(true);
    overrideForm.setFieldsValue({
      watchLadderEpisodesPerLevel: record.watchLadderEpisodesPerLevel ?? null,
      watchLadderBaseCoins: record.watchLadderBaseCoins ?? null,
      watchLadderLevels: record.watchLadderLevels ?? null,
      watchTaskPriority: record.watchTaskPriority ?? null,
    });
  };

  const closeOverrideModal = () => {
    setModalVisible(false);
    setSeriesId(null);
    overrideForm.resetFields();
  };

  const handleOverrideSubmit = async () => {
    try {
      const fields = await overrideForm.validateFields();
      setConfirmLoading(true);
      putSeriesCoinFields(
        apiUrl,
        token,
        seriesId,
        {
          // Blank -> null -> $unset on the doc -> back to the global value.
          watchLadderEpisodesPerLevel: fields.watchLadderEpisodesPerLevel ?? null,
          watchLadderBaseCoins: fields.watchLadderBaseCoins ?? null,
          watchLadderLevels: fields.watchLadderLevels ?? null,
          watchTaskPriority: fields.watchTaskPriority ?? null,
        },
        (result) => {
          setConfirmLoading(false);
          if (result.success) {
            notification.success({
              message: "Success",
              description: "Ladder numbers updated",
            });
            closeOverrideModal();
            setCounter((c) => c + 1);
          } else {
            notification.error({
              message: "Error updating ladder numbers",
              description: "Check console for more details",
            });
          }
        },
        () => {
          setConfirmLoading(false);
          notification.error({
            message: "Error updating ladder numbers",
            description: "Check console for more details",
          });
        }
      );
    } catch (err) {
      notification.error({
        message: "Error updating ladder numbers",
        description: "Fields are incorrect/missing",
      });
    }
  };

  return (
    <div className="manage-tasks-wrapper">
      <Alert
        className="manage-tasks-caption"
        type="info"
        showIcon
        message="Daily escalating reward for the series enabled below: watch X episodes of that series → Y coins, the next X → 2×Y, then 3×Y… up to the configured levels. Each episode counts once per day; the ladder resets at IST midnight and every level's coins credit automatically the moment it completes (subject to the daily earn cap). The numbers below are GLOBAL DEFAULTS — 'Edit numbers' on a series overrides them for that series. Every series' level count is additionally capped by its total episodes ÷ X, so short series automatically show fewer levels (a series shorter than X episodes shows no ladder at all — disable its toggle)."
      />
      <Form
        form={form}
        layout="vertical"
        className="manage-tasks-config-form"
        onFinish={handleSubmit}
      >
        <Form.Item name="enabled" label="Enabled" valuePropName="checked">
          <Switch />
        </Form.Item>
        <Form.Item
          name="episodesPerRung"
          label="Episodes per level (X)"
          extra="Episodes to watch (any series) to complete one level. 0 = ladder off even when enabled."
          rules={[{ type: "integer", min: 0, message: "Must be an integer >= 0" }]}
        >
          <InputNumber min={0} precision={0} placeholder="e.g. 3" />
        </Form.Item>
        <Form.Item
          name="baseCoins"
          label="Base coins (Y)"
          extra="Level 1 pays Y, level 2 pays 2×Y, level 3 pays 3×Y…"
          rules={[{ type: "integer", min: 0, message: "Must be an integer >= 0" }]}
        >
          <InputNumber min={0} precision={0} placeholder="e.g. 50" />
        </Form.Item>
        <Form.Item
          name="maxRungs"
          label="Levels per day"
          extra="How many levels the ladder has. E.g. X=3, Y=50, 3 levels → a full day pays 50+100+150 = 300 coins for 9 episodes."
          rules={[{ type: "integer", min: 1, max: 20, message: "Must be 1–20" }]}
        >
          <InputNumber min={1} max={20} precision={0} placeholder="e.g. 3" />
        </Form.Item>
        <Form.Item
          name="infoPointsText"
          label="Info popup text (English)"
          extra={
            'Shown in the task card\'s "Info" popup, one line per row. Placeholders: ' +
            "{title} = series name, {total} = full-ladder coins, {levels} = level count, " +
            "{x} = episodes per level; a row containing only {level_lines} expands to one " +
            '"Level 1: 50 coins" row per level. Leave empty for the default text.'
          }
          rules={[{ max: 2400, message: "Too long" }]}
        >
          <Input.TextArea
            rows={6}
            placeholder={
              "{title} - {total} coins\n{levels} levels, each level has around {x} episodes.\n{level_lines}\nNote: You must watch the entire video to complete the level."
            }
          />
        </Form.Item>
        <Form.Item
          name="infoPointsHiText"
          label="Info popup text (Hindi)"
          extra="Same placeholders as English ({level_lines} renders as लेवल 1: 50 कॉइन…). Leave empty for the default Hindi text."
          rules={[{ max: 2400, message: "Too long" }]}
        >
          <Input.TextArea
            rows={6}
            placeholder={
              "{title} - {total} कॉइन\n{levels} लेवल, हर लेवल में लगभग {x} एपिसोड।\n{level_lines}\nनोट: लेवल पूरा करने के लिए पूरा वीडियो देखना ज़रूरी है।"
            }
          />
        </Form.Item>
        <Button type="primary" htmlType="submit" loading={saving}>
          Save
        </Button>
      </Form>
      <Alert
        className="manage-tasks-caption"
        type="warning"
        showIcon
        style={{ marginTop: 24 }}
        message="A series shows EITHER the ladder OR its Watch & Earn task — enabling the ladder here overrides that series' Watch & Earn card."
      />
      <Table dataSource={series}>
        <Column title="Title" dataIndex="title" key="title" align="center" />
        <Column
          title="Ladder"
          key="ladder"
          align="center"
          render={(text, record) => (
            <Switch
              checked={Boolean(record.watchLadderEnabled)}
              loading={togglingId === record.key}
              onChange={(checked) => toggleSeries(record, checked)}
            />
          )}
        />
        <Column
          title="Numbers (effective)"
          key="numbers"
          align="center"
          render={(text, record) => {
            const eff = effectiveNumbers(record);
            if (eff.x < 1 || eff.y < 1 || eff.configured < 1) {
              return <Tag>—</Tag>;
            }
            return (
              <Space size={4} direction="vertical">
                <span>
                  {eff.x} eps/level · {eff.y} base ·{" "}
                  {eff.levels < 1 ? "0 levels (too short!)" : `${eff.levels} levels`}
                </span>
                <Space size={4}>
                  {eff.overridden ? (
                    <Tag color="blue">override</Tag>
                  ) : (
                    <Tag>global</Tag>
                  )}
                  {eff.levels < eff.configured ? (
                    <Tag color={eff.levels < 1 ? "red" : "gold"}>
                      capped by {record.numberOfEpisodes || 0} eps
                    </Tag>
                  ) : null}
                </Space>
              </Space>
            );
          }}
        />
        <Column
          title="Priority"
          key="priority"
          align="center"
          render={(text, record) =>
            record.watchTaskPriority ? (
              <Tag color="purple">#{record.watchTaskPriority}</Tag>
            ) : (
              "—"
            )
          }
        />
        <Column
          title="Note"
          key="note"
          align="center"
          render={(text, record) => {
            if (record.watchLadderEnabled) {
              return record.watchEarnEpisodes > 0 && record.watchEarnCoins > 0 ? (
                <Tag color="orange">Overrides Watch &amp; Earn</Tag>
              ) : (
                <Tag color="green">Ladder active</Tag>
              );
            }
            return record.watchEarnEpisodes > 0 && record.watchEarnCoins > 0 ? (
              <Tag>Watch &amp; Earn</Tag>
            ) : (
              <Tag>—</Tag>
            );
          }}
        />
        <Column
          title="Actions"
          key="actions"
          align="center"
          render={(text, record) => (
            <Space size="middle">
              <Typography.Link onClick={() => showOverrideModal(record)}>
                Edit numbers
              </Typography.Link>
            </Space>
          )}
        />
      </Table>
      <Modal
        title={`Ladder numbers — ${seriesTitle}`}
        visible={modalVisible}
        onOk={handleOverrideSubmit}
        okText="Submit"
        onCancel={closeOverrideModal}
        confirmLoading={confirmLoading}
        destroyOnClose={true}
        maskClosable={false}
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Blank fields use the global numbers above. Whatever the level count, this series' ladder is capped at its total episodes ÷ episodes-per-level (an episode counts once per day)."
        />
        <Form form={overrideForm} layout="vertical">
          <Form.Item
            name="watchLadderEpisodesPerLevel"
            label="Episodes per level (X)"
            extra={`Blank = global (${globalCfg.episodesPerRung || "unset"})`}
            rules={[{ type: "integer", min: 1, message: "Must be an integer >= 1" }]}
          >
            <InputNumber min={1} precision={0} placeholder="use global" />
          </Form.Item>
          <Form.Item
            name="watchLadderBaseCoins"
            label="Base coins (Y)"
            extra={`Level 1 pays Y, level 2 pays 2×Y… Blank = global (${globalCfg.baseCoins || "unset"})`}
            rules={[{ type: "integer", min: 1, message: "Must be an integer >= 1" }]}
          >
            <InputNumber min={1} precision={0} placeholder="use global" />
          </Form.Item>
          <Form.Item
            name="watchLadderLevels"
            label="Levels per day"
            extra={`Blank = global (${globalCfg.maxRungs || "unset"})`}
            rules={[{ type: "integer", min: 1, max: 20, message: "Must be 1–20" }]}
          >
            <InputNumber min={1} max={20} precision={0} placeholder="use global" />
          </Form.Item>
          <Form.Item
            name="watchTaskPriority"
            label="Priority (display order)"
            extra="Lower shows first in the app's Tasks section — the top 2 cards are inline, the rest sit behind 'All'. Blank = unprioritized (after every prioritized series). A viewer's claimable / in-progress series always jump ahead regardless."
            rules={[{ type: "integer", min: 1, message: "Must be an integer >= 1" }]}
          >
            <InputNumber min={1} precision={0} placeholder="unprioritized" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default WatchLadderConfig;
